import { create, enumFromJson, enumToJson } from "@bufbuild/protobuf";
import { Chat, ChatPrompt, Features, type LanguageRegions, RichText, Scope } from "./gen";

/**
 * Chat mode type matching the PromptMode proto enum values.
 * When a mode is provided, the backend resolves model, thinking config,
 * and enabled tools from server-side mode configurations.
 */
export type ChatMode = "auto" | "fast" | "expert" | "research";

const CHAT_MODE_TO_PROTO: Record<ChatMode, ChatPrompt.PromptMode> = {
  auto: ChatPrompt.PromptMode.AUTO,
  fast: ChatPrompt.PromptMode.FAST,
  expert: ChatPrompt.PromptMode.EXPERT,
  research: ChatPrompt.PromptMode.RESEARCH,
};

/**
 * Vendor preference type matching the VendorPreference proto enum values.
 * When set, the backend prioritizes models from the preferred vendor.
 */
export type VendorPreferenceType = "auto" | "anthropic" | "openai" | "google";

const VENDOR_PREF_TO_PROTO: Record<VendorPreferenceType, ChatPrompt.VendorPreference> = {
  auto: ChatPrompt.VendorPreference.UNSPECIFIED,
  anthropic: ChatPrompt.VendorPreference.ANTHROPIC,
  openai: ChatPrompt.VendorPreference.OPENAI,
  google: ChatPrompt.VendorPreference.GOOGLE,
};

/**
 * Vendor strategy type matching the VendorStrategy proto enum values.
 * When set, controls how strictly the vendor preference is applied.
 */
export type VendorStrategyType = "prefer" | "only";

const VENDOR_STRATEGY_TO_PROTO: Record<VendorStrategyType, ChatPrompt.VendorStrategy> = {
  prefer: ChatPrompt.VendorStrategy.PREFER,
  only: ChatPrompt.VendorStrategy.ONLY,
};

/**
 * A model pinned within the tier and vendor the router resolves to. The router
 * reorders that tier's preference list rather than bypassing it, so a pin the
 * resolved list doesn't offer is ignored server-side rather than rejected.
 * UNSPECIFIED means no pin.
 */
export type PreferredModel = Features.Feature;

export const NO_PREFERRED_MODEL: PreferredModel = Features.Feature.UNSPECIFIED;

const CHAT_MODE_FROM_PROTO: Partial<Record<ChatPrompt.PromptMode, ChatMode>> = {
  [ChatPrompt.PromptMode.AUTO]: "auto",
  [ChatPrompt.PromptMode.FAST]: "fast",
  [ChatPrompt.PromptMode.EXPERT]: "expert",
  [ChatPrompt.PromptMode.RESEARCH]: "research",
};

const VENDOR_PREF_FROM_PROTO: Partial<Record<ChatPrompt.VendorPreference, VendorPreferenceType>> = {
  [ChatPrompt.VendorPreference.UNSPECIFIED]: "auto",
  [ChatPrompt.VendorPreference.ANTHROPIC]: "anthropic",
  [ChatPrompt.VendorPreference.OPENAI]: "openai",
  [ChatPrompt.VendorPreference.GOOGLE]: "google",
};

const VENDOR_STRATEGY_FROM_PROTO: Partial<Record<ChatPrompt.VendorStrategy, VendorStrategyType>> = {
  [ChatPrompt.VendorStrategy.PREFER]: "prefer",
  [ChatPrompt.VendorStrategy.ONLY]: "only",
};

/** Undefined for an unset or unrecognised mode, so callers pick their own fallback. */
export function chatModeFromProto(mode: number | undefined): ChatMode | undefined {
  return mode === undefined ? undefined : CHAT_MODE_FROM_PROTO[mode as ChatPrompt.PromptMode];
}

/** UNSPECIFIED maps to "auto" — the absence of a vendor preference is one. */
export function vendorPreferenceFromProto(vendor: number | undefined): VendorPreferenceType | undefined {
  return vendor === undefined ? undefined : VENDOR_PREF_FROM_PROTO[vendor as ChatPrompt.VendorPreference];
}

export function vendorStrategyFromProto(strategy: number | undefined): VendorStrategyType | undefined {
  return strategy === undefined ? undefined : VENDOR_STRATEGY_FROM_PROTO[strategy as ChatPrompt.VendorStrategy];
}

/**
 * The user settings store keys models by their entitlement feature name. Only a real pin reaches
 * here — no pin is stored as the absence of a row, not a value.
 */
export function preferredModelToSettingValue(model: PreferredModel): string {
  const name = enumToJson(Features.FeatureSchema, model);
  // enumToJson is null only for google.protobuf.NullValue; Feature's JSON form is always its name.
  if (typeof name !== "string") throw new Error(`Feature ${model} has no JSON name`);
  return name;
}

export function preferredModelFromSettingValue(value: string | undefined): PreferredModel {
  if (!value) return NO_PREFERRED_MODEL;
  try {
    return enumFromJson(Features.FeatureSchema, value);
  } catch {
    // A retired or unrecognised model reads as no pin, the same way the router treats a model it
    // can't resolve.
    return NO_PREFERRED_MODEL;
  }
}

/**
 * Creates a StreamRequest for starting a new chat with rich text content.
 * If rich text, rich text documents may be returned for some stop messages
 * representing the entire message in rich text format.
 *
 * @param chatId - Chat ID (empty string for new chat)
 * @param text - The text content to send
 * @param languageRegion - Language region for the chat
 * @param projectId - Project ID for the chat to be saved to
 * @param workspaceId - Workspace ID for the chat to be saved to
 * @param scope - Optional scope information for knowledge filtering
 * @param parentMessageId - Optional parent message ID for threading
 * @param suggestionAttribution - Optional attribution for suggestion prompts
 * @param preLoadedSkills - Optional skill IDs to pre-load before LLM execution
 * @param mode - Optional chat mode (auto/fast/expert/research). Backend resolves model/tools/thinking from mode.
 * @param vendorPreference - Optional vendor preference for model selection within the mode.
 * @param vendorStrategy - Optional strategy for how strictly the vendor preference is applied.
 * @param preferredModel - Optional model pin, scoped to the resolved tier and vendor.
 */
export function newStreamRequest(
  chatId: string,
  text: string | RichText.RichText,
  languageRegion: LanguageRegions.LanguageRegion,
  projectId: string,
  workspaceId: string,
  scope?: Scope.Scope,
  parentMessageId?: string,
  suggestionAttribution?: { persona: string; category: string },
  preLoadedSkills?: string[],
  mode?: ChatMode,
  vendorPreference?: VendorPreferenceType,
  vendorStrategy?: VendorStrategyType,
  memoryConfig?: ChatPrompt.MemoryConfig,
  webSearchEnabled?: boolean,
  preferredModel?: PreferredModel,
  retryOfMessageId?: string,
  attachments?: { artifactId: string; mimeType: string; name: string; addToKnowledge: boolean }[]
): Chat.StreamRequest {
  const resolvedMode = mode ? CHAT_MODE_TO_PROTO[mode] : undefined;
  const resolvedVendor = vendorPreference ? VENDOR_PREF_TO_PROTO[vendorPreference] : undefined;
  const resolvedStrategy = vendorStrategy ? VENDOR_STRATEGY_TO_PROTO[vendorStrategy] : undefined;

  const suggestionAttr = suggestionAttribution
    ? create(ChatPrompt.SuggestionAttributionSchema, {
        persona: suggestionAttribution.persona,
        category: suggestionAttribution.category,
      })
    : undefined;

  // Normalize through ScopeSchema so optional prompt.scope is encoded when the nested message
  // has non-default fields (plain objects can be merged away for optional submessages).
  const promptScope = scope !== undefined ? create(Scope.ScopeSchema, scope) : undefined;

  const disabledToolsOverride =
    webSearchEnabled === false ? create(ChatPrompt.EnabledToolsSchema, { webSearch: true }) : undefined;

  return create(Chat.StreamRequestSchema, {
    prompt: create(ChatPrompt.PromptSchema, {
      chatId,
      languageRegion,
      parentMessageId,
      projectId: projectId,
      workspaceId: workspaceId,
      prompt: {
        case: "richText",
        value:
          typeof text === "string"
            ? create(RichText.RichTextSchema, {
                type: "doc",
                content: [
                  create(RichText.NodeSchema, {
                    type: "paragraph",
                    content: [
                      create(RichText.NodeSchema, {
                        type: "text",
                        text,
                      }),
                    ],
                  }),
                ],
                attrs: create(RichText.DocAttrsSchema, {
                  title: "",
                  createdAt: BigInt(Date.now()),
                  updatedAt: BigInt(Date.now()),
                  version: "1.0",
                }),
              })
            : {
                ...text,
                attrs: create(RichText.DocAttrsSchema, {
                  title: "",
                  createdAt: BigInt(Date.now()),
                  updatedAt: BigInt(Date.now()),
                  version: "1.0",
                }),
              },
      },
      scope: promptScope,
      recordSession: true,
      mode: resolvedMode,
      vendorPreference: resolvedVendor,
      vendorStrategy: resolvedStrategy,
      suggestionAttribution: suggestionAttr,
      preLoadedSkills: preLoadedSkills || [],
      memoryConfig,
      disabledTools: disabledToolsOverride,
      preferredModel,
      retryOfMessageId,
      attachments,
    }),
  });
}

/**
 * Creates a StreamPlaybackRequest for playing back a previously streamed message.
 *
 * @param messageId - The ID of the message to play back (Format: ^message_[a-z0-9]{20}$)
 * @param chatId - The ID of the chat containing the message (Format: ^chat_[a-z0-9]{20}$)
 * @param playbackSpeed - Playback speed multiplier (1.0 = normal speed, 0.5 = half speed, 2.0 = double speed)
 */
export function newStreamPlaybackRequest(
  messageId: string,
  chatId: string,
  playbackSpeed: number
): Chat.StreamPlaybackRequest {
  return create(Chat.StreamPlaybackRequestSchema, {
    messageId,
    chatId,
    playbackSpeed,
  });
}

/**
 * Creates a protobuf MemoryConfig object from a simple JavaScript object.
 * This utility helps avoid importing @bufbuild/protobuf directly in application code.
 * When the MemoryConfig is absent from the prompt entirely, all memory scopes default
 * to enabled. The booleans control explicit enable/disable per scope.
 *
 * @param config - Object with boolean flags for each memory scope
 */
export function newMemoryConfig(config: { userMemory: boolean; projectMemory: boolean }): ChatPrompt.MemoryConfig {
  return create(ChatPrompt.MemoryConfigSchema, {
    userMemory: config.userMemory,
    projectMemory: config.projectMemory,
  });
}
